import type { PremiseConfig } from "@shared/types/foyer";

/**
 * A change to a premise, as the paths that differ between two copies.
 *
 * Many editors are written as "here is the premise as it should now be"
 * (`onChange(next)`). Saving that as `{...saved, ...next}` would overwrite
 * anything else that changed in the meantime: two switches flipped in quick
 * succession would each be computed from the same stale copy, and the second
 * write would undo the first. {@link patchPremise} instead records only what
 * THIS edit changed and replays exactly that onto whatever is saved when the
 * write runs, so edits to different fields merge.
 *
 * Granularity: plain objects are compared key by key, recursively. Arrays and
 * primitives are replaced whole - an ordered list is one value, and merging two
 * people's edits of the same list item by item would invent orderings nobody
 * chose. Two edits of the SAME array or field are last-writer-wins, which is
 * what a save is.
 */

type Json = unknown;
type Op = { path: string[]; value: Json; remove: boolean };

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

function diff(before: Json, after: Json, path: string[], out: Op[]) {
  if (isPlainObject(before) && isPlainObject(after)) {
    for (const key of Object.keys(after)) {
      if (!(key in before)) {
        if (after[key] !== undefined)
          out.push({ path: [...path, key], value: after[key], remove: false });
      } else {
        diff(before[key], after[key], [...path, key], out);
      }
    }
    for (const key of Object.keys(before)) {
      if (!(key in after) || after[key] === undefined) {
        if (before[key] !== undefined)
          out.push({ path: [...path, key], value: undefined, remove: true });
      }
    }
    return;
  }
  if (!same(before, after)) {
    out.push({ path, value: after, remove: after === undefined });
  }
}

function applyOp(target: Record<string, unknown>, op: Op) {
  let node = target;
  for (let i = 0; i < op.path.length - 1; i++) {
    const key = op.path[i]!;
    const next = node[key];
    // Copy on the way down: the saved config must not be mutated in place.
    node[key] = isPlainObject(next) ? { ...next } : {};
    node = node[key] as Record<string, unknown>;
  }
  const last = op.path[op.path.length - 1]!;
  if (op.remove) delete node[last];
  else node[last] = op.value;
}

/**
 * The update that turns `before` into `after`, expressed so it can be applied to
 * a different copy of the premise (the one that is saved when the write runs).
 * Returns an identity function when nothing differs.
 */
export function patchPremise(
  before: PremiseConfig,
  after: PremiseConfig
): (current: PremiseConfig) => PremiseConfig {
  const ops: Op[] = [];
  diff(before, after, [], ops);
  return (current) => {
    if (ops.length === 0) return current;
    const next = { ...current } as Record<string, unknown>;
    for (const op of ops) applyOp(next, op);
    return next as unknown as PremiseConfig;
  };
}

/** True when `after` differs from `before` at all. */
export function premiseChanged(
  before: PremiseConfig,
  after: PremiseConfig
): boolean {
  const ops: Op[] = [];
  diff(before, after, [], ops);
  return ops.length > 0;
}
